// MCP Security — Policy library.
//
// Grouped grid of policy packs the operator can apply to an endpoint
// (compliance, hardened, dev-friendly, ai-agent-abuse, data-exfil,
// ssrf). Clicking "Apply pack" opens a dialog with the real per-
// endpoint diff pulled from GET /mcp/policy-library/:slug/preview.
//
// Wired 2026-10-11 to the MCP-UX routes:
//   GET /mcp/policy-library
//   POST /mcp/policy-library/:slug/preview
//   POST /mcp/policy-library/:slug/apply

import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, Alert, Button, Dialog, DialogContent, LinearProgress,
  Checkbox, FormControlLabel,
} from "@mui/material";

import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import axios from "../../../../../utils/axios/v1/userAxios";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import tokens from "./_shared/tokens";
import useMcpQuery from "./_shared/useMcpQuery";
import { primaryButtonStyle, secondaryButtonStyle } from "./_shared/buttonStyles";

const CATEGORY_META = {
  starter: {
    label: "Posture presets",
    desc: "One-click defaults for common MCP postures.",
  },
  threat: {
    label: "Threat packs",
    desc: "Policies clamped to a specific attack shape.",
  },
};

const ICON_FOR_SLUG = (slug) => {
  if (slug === "hardened" || slug === "compliance") return <ShieldOutlinedIcon fontSize="small" />;
  if (slug === "dev-friendly") return <PolicyOutlinedIcon fontSize="small" />;
  if (slug === "data-exfil" || slug === "ssrf") return <TuneOutlinedIcon fontSize="small" />;
  if (slug === "ai-agent-abuse") return <HubOutlinedIcon fontSize="small" />;
  return <LibraryBooksOutlinedIcon fontSize="small" />;
};

function formatPatchValue(key, v) {
  if (v == null) return "—";
  if (key === "response_cap_bytes") {
    const n = Number(v);
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
    if (n >= 1024) return `${(n / 1024).toFixed(0)} KiB`;
    return `${n} B`;
  }
  if (key === "sampling_rpm_cap") return v === 0 ? "disabled" : `${v} rpm`;
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

function PackApplyDialog({ pack, siteUid, onClose, onApplied }) {
  const [endpoints, setEndpoints] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [epLoading, setEpLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState(null);
  const [applied, setApplied] = useState(false);

  React.useEffect(() => {
    if (!pack || !siteUid) return;
    setEpLoading(true);
    axios.get(`mcp/sites/${siteUid}/endpoints`)
      .then((r) => {
        const items = (r.data.items || r.data.data || []).filter((e) => e.state !== "disabled");
        setEndpoints(items);
      })
      .catch((e) => setErr(e?.response?.data?.message || e.message))
      .finally(() => setEpLoading(false));
  }, [pack, siteUid]);

  React.useEffect(() => {
    if (!pack || selected.size === 0) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    axios.post(`mcp/policy-library/${pack.slug}/preview`, { endpoint_ids: [...selected] })
      .then((r) => setPreview(r.data?.data))
      .catch((e) => setErr(e?.response?.data?.message || e.message))
      .finally(() => setPreviewLoading(false));
  }, [pack, selected]);

  const apply = async () => {
    if (!pack || selected.size === 0) return;
    setApplying(true);
    setErr(null);
    try {
      await axios.post(`mcp/policy-library/${pack.slug}/apply`, { endpoint_ids: [...selected] });
      setApplied(true);
      onApplied && onApplied(pack);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally { setApplying(false); }
  };

  if (!pack) return null;

  return (
    <Dialog open={!!pack} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 0.5 }}>Apply "{pack.name}"</Typography>
        <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 2 }}>
          {pack.description}
        </Typography>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}

        <Typography sx={{ fontSize: 12, color: tokens.neutral[800], fontWeight: 500, mb: 0.5 }}>
          Target endpoints
        </Typography>
        {epLoading && <LinearProgress />}
        <Box sx={{ border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", maxHeight: 180, overflowY: "auto", mb: 2 }}>
          {endpoints.length === 0 && !epLoading && (
            <Typography sx={{ p: 2, fontSize: 12, color: tokens.neutral[700] }}>No MCP endpoints to apply to.</Typography>
          )}
          {endpoints.map((e) => {
            const on = selected.has(e.endpoint_id);
            return (
              <FormControlLabel
                key={e.endpoint_id}
                sx={{ display: "flex", px: 1, py: 0.25, m: 0, borderBottom: `1px solid ${tokens.neutral[150]}` }}
                control={
                  <Checkbox
                    size="small"
                    checked={on}
                    onChange={() => setSelected((prev) => {
                      const next = new Set(prev);
                      if (on) next.delete(e.endpoint_id); else next.add(e.endpoint_id);
                      return next;
                    })}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12 }}>
                    {e.location_pattern || e.endpoint_url || e.endpoint_id}
                  </Typography>
                }
              />
            );
          })}
        </Box>

        <Typography sx={{ fontSize: 12, color: tokens.neutral[800], fontWeight: 500, mb: 0.5 }}>
          Per-endpoint diff
        </Typography>
        {previewLoading && <LinearProgress />}
        {!previewLoading && (!preview?.endpoints?.length) && (
          <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 2 }}>
            Select at least one endpoint to preview the diff.
          </Typography>
        )}
        <Box sx={{ border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden", mb: 2, maxHeight: 240, overflowY: "auto" }}>
          {(preview?.endpoints || []).map((ep) => (
            <Box key={ep.endpoint_id} sx={{ borderBottom: `1px solid ${tokens.neutral[150]}` }}>
              <Typography sx={{ px: 1.25, pt: 0.75, pb: 0.25, fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[700] }}>
                {ep.endpoint_id.slice(0, 10)}
              </Typography>
              {Object.keys(ep.diff || {}).length === 0 && (
                <Typography sx={{ px: 1.25, pb: 1, fontSize: 11, color: "#167F47" }}>No changes — already matches.</Typography>
              )}
              {Object.entries(ep.diff || {}).map(([k, v]) => (
                <Stack key={k} direction="row" spacing={1} sx={{ px: 1.25, py: 0.5, fontSize: 12 }}>
                  <Typography sx={{ flex: 1, fontFamily: tokens.fontMono }}>{k}</Typography>
                  <Typography sx={{ fontFamily: tokens.fontMono, color: tokens.neutral[700] }}>{formatPatchValue(k, v.before)}</Typography>
                  <span style={{ color: tokens.neutral[500] }}>→</span>
                  <Typography sx={{ fontFamily: tokens.fontMono, color: "#167F47" }}>{formatPatchValue(k, v.after)}</Typography>
                </Stack>
              ))}
            </Box>
          ))}
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button sx={secondaryButtonStyle} onClick={onClose} disabled={applying}>
            {applied ? "Close" : "Cancel"}
          </Button>
          {!applied && (
            <Button sx={primaryButtonStyle} onClick={apply} disabled={applying || selected.size === 0}>
              {applying ? "Applying…" : `Apply pack`}
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function McpPolicyLibrary() {
  const { effectiveSiteUid } = useGlobalSite();
  const [applying, setApplying] = useState(null);
  const [notice, setNotice] = useState(null);

  const { data: resp, error, loading } = useMcpQuery("mcp/policy-library");
  const packs = resp?.data || [];
  const grouped = useMemo(() => {
    const out = {};
    for (const p of packs) {
      const cat = p.category || "misc";
      (out[cat] = out[cat] || []).push(p);
    }
    return out;
  }, [packs]);

  return (
    <>
      <Helmet title="MCP Security — Policy library" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Policy library"
          description="Opinionated policy packs you can apply to an MCP endpoint in one click."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header to apply policy packs.</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
              {loading && <LinearProgress />}
              {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

              <Stack spacing={4}>
                {Object.entries(grouped).map(([cat, catPacks]) => {
                  const meta = CATEGORY_META[cat] || { label: cat, desc: "" };
                  return (
                    <Stack key={cat} spacing={1.5}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                          {meta.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>{meta.desc}</Typography>
                        <Box sx={{ flex: 1, height: 1, background: tokens.neutral[300] }} />
                      </Stack>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 1.5 }}>
                        {catPacks.map((p) => {
                          const fields = Object.keys(p.settings_patch || {});
                          return (
                            <Box
                              key={p.slug}
                              sx={{
                                background: "#fff",
                                border: `1px solid ${tokens.neutral[200]}`,
                                borderRadius: "8px",
                                p: 2.25,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <Box sx={{ width: 38, height: 38, borderRadius: "8px", background: tokens.primary[100], color: tokens.primaryIndigo, display: "grid", placeItems: "center" }}>
                                  {ICON_FOR_SLUG(p.slug)}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>
                                    {p.name}
                                    {p.is_default && (
                                      <span style={{ fontSize: 10, fontWeight: 500, padding: "1px 6px", borderRadius: 999, background: tokens.primary[100], color: tokens.primaryIndigoInk, marginLeft: 6 }}>
                                        default
                                      </span>
                                    )}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                                    {fields.length} settings
                                  </Typography>
                                </Box>
                              </Stack>
                              <Typography sx={{ fontSize: 13, color: tokens.neutral[800] }}>
                                {p.description}
                              </Typography>
                              <Box sx={{ border: `1px solid ${tokens.neutral[150]}`, borderRadius: "8px", overflow: "hidden" }}>
                                {fields.slice(0, 4).map((k, i, arr) => (
                                  <Stack
                                    key={k}
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                      px: 1.25, py: 0.75,
                                      borderBottom: i === arr.length - 1 ? "none" : `1px solid ${tokens.neutral[150]}`,
                                      fontSize: 12,
                                    }}
                                  >
                                    <Typography sx={{ flex: 1, fontSize: 12, color: tokens.neutral[800] }}>{k}</Typography>
                                    <Typography sx={{ fontFamily: tokens.fontMono, color: tokens.neutral[900], textAlign: "right", maxWidth: "55%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}>
                                      {formatPatchValue(k, p.settings_patch[k])}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Box>
                              <Button
                                sx={secondaryButtonStyle}
                                disabled={!effectiveSiteUid}
                                onClick={() => setApplying(p)}
                              >
                                Apply pack
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <PackApplyDialog
        pack={applying}
        siteUid={effectiveSiteUid}
        onClose={() => setApplying(null)}
        onApplied={(p) => setNotice(`Applied "${p.name}" to selected endpoints.`)}
      />
    </>
  );
}
